import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  Accordion,
  AccordionBody,
  AccordionHeader,
  AccordionItem,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Table,
  UncontrolledTooltip,
} from "reactstrap";

import {
  getHelpArticleUrl,
  HELP_ARTICLE_TEAM_PERMISSIONS,
} from "../../../helpers/helpCenter";
import {
  isOwnerOnlyPermission,
  matrixHasPermissions,
  permissionTooltipKey,
} from "../../../helpers/teamPermissionUtils";
import { TeamPermissionMatrix } from "../../../services/permissionService";

interface TeamPermissionMatrixViewProps {
  matrix: TeamPermissionMatrix;
  showHelpLink?: boolean;
  compact?: boolean;
}

const TeamPermissionMatrixView: React.FC<TeamPermissionMatrixViewProps> = ({
  matrix,
  showHelpLink = true,
  compact = false,
}) => {
  const { t, i18n } = useTranslation();
  const [openGroup, setOpenGroup] = useState<string>("0");

  const roleColumns = useMemo(() => matrix.roles, [matrix.roles]);

  if (!matrixHasPermissions(matrix)) {
    return (
      <div className="text-center py-5">
        <i className="ri-shield-keyhole-line display-4 text-muted" />
        <h5 className="mt-3">{t("team_permissions.empty.title")}</h5>
        <p className="text-muted mb-3">{t("team_permissions.empty.description")}</p>
        <Button color="primary" tag={Link} to="/users">
          {t("team_permissions.empty.cta")}
        </Button>
      </div>
    );
  }

  return (
    <>
      {showHelpLink ? (
        <p className="text-muted mb-3">
          {t("team_permissions.intro")}{" "}
          <a
            href={getHelpArticleUrl(HELP_ARTICLE_TEAM_PERMISSIONS, i18n.language)}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t("team_permissions.helpLink")}
          </a>
        </p>
      ) : null}

      {compact ? (
        matrix.groups.map((group, index) => (
          <Card key={group.name} className="mb-3">
            <CardHeader className="py-2">
              <h6 className="card-title mb-0">{group.display_name}</h6>
            </CardHeader>
            <CardBody className="p-0">
              <PermissionGroupTable
                group={group}
                roleColumns={roleColumns}
                idPrefix={`compact-${index}`}
              />
            </CardBody>
          </Card>
        ))
      ) : (
        <Accordion open={openGroup} toggle={setOpenGroup} className="accordion-flush">
          {matrix.groups.map((group, index) => (
            <AccordionItem key={group.name}>
              <AccordionHeader targetId={String(index)}>
                <span className="fw-medium">{group.display_name}</span>
                <Badge color="soft-secondary" className="ms-2">
                  {group.permissions.length}
                </Badge>
              </AccordionHeader>
              <AccordionBody accordionId={String(index)}>
                <PermissionGroupTable
                  group={group}
                  roleColumns={roleColumns}
                  idPrefix={`accordion-${index}`}
                />
              </AccordionBody>
            </AccordionItem>
          ))}
        </Accordion>
      )}
    </>
  );
};

interface PermissionGroupTableProps {
  group: TeamPermissionMatrix["groups"][number];
  roleColumns: TeamPermissionMatrix["roles"];
  idPrefix: string;
}

const PermissionGroupTable: React.FC<PermissionGroupTableProps> = ({
  group,
  roleColumns,
  idPrefix,
}) => {
  const { t } = useTranslation();

  return (
    <div className="table-responsive">
      <Table hover className="align-middle mb-0">
        <thead className="table-light">
          <tr>
            <th>{t("team_permissions.table.permission")}</th>
            {roleColumns.map((role) => (
              <th key={role.name} className="text-center" style={{ minWidth: 110 }}>
                {role.display_name}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {group.permissions.map((permission) => {
            const tooltipKey = `team_permissions.tooltips.${permissionTooltipKey(permission.name)}`;
            const tooltipText = t(tooltipKey, { defaultValue: "" });
            const labelId = `${idPrefix}-perm-${permissionTooltipKey(permission.name)}`;
            const ownerOnly = isOwnerOnlyPermission(permission);

            return (
              <tr key={permission.name}>
                <td>
                  <span id={tooltipText ? labelId : undefined}>{permission.display_name}</span>
                  {ownerOnly ? (
                    <Badge color="soft-warning" className="ms-2">
                      {t("team_permissions.badges.ownerOnly")}
                    </Badge>
                  ) : null}
                  {tooltipText ? (
                    <>
                      <i
                        className="ri-information-line text-muted ms-1"
                        id={`${labelId}-icon`}
                        aria-hidden="true"
                      />
                      <UncontrolledTooltip placement="top" target={labelId}>
                        {tooltipText}
                      </UncontrolledTooltip>
                      <UncontrolledTooltip placement="top" target={`${labelId}-icon`}>
                        {tooltipText}
                      </UncontrolledTooltip>
                    </>
                  ) : null}
                </td>
                {roleColumns.map((role) => {
                  const granted = permission.granted_roles.includes(role.name);

                  return (
                    <td key={role.name} className="text-center">
                      {granted ? (
                        <i
                          className="ri-checkbox-circle-fill text-success fs-5"
                          title={t("team_permissions.table.granted")}
                          aria-label={t("team_permissions.table.granted")}
                        />
                      ) : (
                        <i
                          className="ri-close-circle-line text-muted fs-5"
                          title={t("team_permissions.table.denied")}
                          aria-label={t("team_permissions.table.denied")}
                        />
                      )}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </Table>
    </div>
  );
};

export default TeamPermissionMatrixView;
